<script setup lang="ts">
import { ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Password from 'primevue/password';
import Button from 'primevue/button';
import FileUpload from 'primevue/fileupload';
import UserAvatar from '@/components/UserAvatar.vue';
import { changePassword, updateProfile } from '@/lib/apiClient';
import { compressImageFile } from '@/lib/compressImage';
import { MAX_AVATAR_FILE_BYTES, MAX_AVATAR_FILE_MB } from '@/lib/imageLimits';
import { useAuth } from '@/composables/useAuth';

const toast = useToast();
const { token, user, updateUser } = useAuth();

const nickname = ref(user.value?.nickname ?? '');
const about = ref(user.value?.about ?? '');
const oldPassword = ref('');
const newPassword = ref('');
const savingProfile = ref(false);
const savingPassword = ref(false);
const compressingAvatar = ref(false);

async function onAvatarSelect(event: { files: File[] }) {
  const file = event.files[0];
  if (!file || !token.value) return;

  if (file.size > MAX_AVATAR_FILE_BYTES) {
    toast.add({
      severity: 'warn',
      summary: 'Файл слишком большой',
      detail: `Максимум ${MAX_AVATAR_FILE_MB} МБ`,
      life: 3000,
    });
    return;
  }

  compressingAvatar.value = true;
  try {
    const avatar = await compressImageFile(file);
    await saveProfile({ avatar });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Не удалось обработать фото',
      detail: error instanceof Error ? error.message : 'Ошибка сжатия',
      life: 3000,
    });
  } finally {
    compressingAvatar.value = false;
  }
}

async function saveProfile(extra: { avatar?: string | null } = {}) {
  if (!token.value) return;
  savingProfile.value = true;

  try {
    const result = await updateProfile(token.value, {
      nickname: nickname.value,
      about: about.value || null,
      ...extra,
    });
    updateUser(result.user, result.token);
    toast.add({ severity: 'success', summary: 'Профиль обновлён', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось сохранить',
      life: 3000,
    });
  } finally {
    savingProfile.value = false;
  }
}

async function savePassword() {
  if (!token.value) return;
  savingPassword.value = true;

  try {
    await changePassword(token.value, oldPassword.value, newPassword.value);
    oldPassword.value = '';
    newPassword.value = '';
    toast.add({ severity: 'success', summary: 'Пароль изменён', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось сменить пароль',
      life: 3000,
    });
  } finally {
    savingPassword.value = false;
  }
}
</script>

<template>
  <div class="page">
    <section class="page__hero">
      <h1>Настройки аккаунта</h1>
      <p>Аватар, никнейм, пароль и короткое «о себе».</p>
    </section>

    <Card>
      <template #title>Профиль</template>
      <template #content>
        <div class="profile-head">
          <UserAvatar v-if="user" :nickname="user.nickname" :avatar="user.avatar" size="xlarge" />
          <FileUpload
            mode="basic"
            accept="image/*"
            :choose-label="compressingAvatar ? 'Сжимаем…' : 'Загрузить аватар'"
            :disabled="compressingAvatar || savingProfile"
            custom-upload
            auto
            @select="onAvatarSelect"
          />
        </div>

        <label class="field">
          <span>Никнейм</span>
          <InputText v-model="nickname" fluid />
        </label>

        <label class="field">
          <span>О себе</span>
          <Textarea v-model="about" rows="4" auto-resize placeholder="Необязательно" fluid />
        </label>
      </template>
      <template #footer>
        <Button label="Сохранить профиль" icon="pi pi-check" :loading="savingProfile" @click="saveProfile()" />
      </template>
    </Card>

    <Card>
      <template #title>Смена пароля</template>
      <template #content>
        <label class="field">
          <span>Текущий пароль</span>
          <Password v-model="oldPassword" :feedback="false" toggle-mask fluid />
        </label>
        <label class="field">
          <span>Новый пароль</span>
          <Password v-model="newPassword" :feedback="false" toggle-mask fluid />
        </label>
      </template>
      <template #footer>
        <Button label="Сменить пароль" icon="pi pi-lock" :loading="savingPassword" @click="savePassword" />
      </template>
    </Card>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 720px;
}

.page__hero h1 {
  margin: 0 0 0.35rem;
  color: #831843;
}

.page__hero p {
  margin: 0;
  color: #9d174d;
}

.profile-head {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #831843;
  font-weight: 600;
}
</style>
